import { useEffect, useMemo } from 'react';
import { useUserProfileStore } from 'stores/userProfileStore';

/**
 * Returns current user ids (for matching comment author / board member) and the
 * profile picture URL. Use to show the logged-in user's pfp in Kanban avatars.
 */
export function useCurrentUserAvatar(): {
  currentUserIds: Set<string>;
  profilePictureUrl: string | null;
} {
  const profile = useUserProfileStore((s) => s.profile);
  const profilePictureUrl = useUserProfileStore((s) => s.profilePictureUrl);
  const getProfilePictureDownloadUrl = useUserProfileStore(
    (s) => s.getProfilePictureDownloadUrl,
  );

  const currentUserIds = useMemo(() => {
    if (!profile) return new Set<string>();
    const ids: string[] = [
      profile.id,
      typeof profile.authUser === 'string'
        ? profile.authUser
        : (profile.authUser?.id ?? ''),
    ].filter(Boolean);
    return new Set(ids);
  }, [profile]);

  useEffect(() => {
    if (profile?.profilePicturePath) {
      void getProfilePictureDownloadUrl();
    }
  }, [profile?.profilePicturePath, getProfilePictureDownloadUrl]);

  return { currentUserIds, profilePictureUrl };
}

export function isCurrentUser(
  currentUserIds: Set<string>,
  authorIdOrUserId: string | undefined,
): boolean {
  return Boolean(authorIdOrUserId && currentUserIds.has(authorIdOrUserId));
}
